import { useState } from 'react';
import { RotateCcw, ShieldAlert, Loader2, Check, Ban, Play } from 'lucide-react';
import { toast } from 'sonner';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useRefundRequestListQuery } from '@/client/api/payment/payment.query';
import { useResolveRefundRequestMutation } from '@/client/api/payment/payment.mutation';
import {
  RefundRequest,
  RefundRequestAction,
  RefundRequestListParams,
} from '@/client/types/payment';
import { usePaymentListUrlState } from '@/client/hooks/usePaymentListUrlState';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import { useHasPermission } from '@/client/hooks/useHasPermission';
import Pagination from '@/client/components/common/Pagination';
import { generateIdempotencyKey } from '@/client/utils/requestSecurity';
import { PaymentsFilterBar } from '@/client/components/admin/Payment/PaymentsFilterBar';
import { RelationshipLink, UserRelationshipLink } from '@/client/components/admin/Payment/RelationshipLink';

const STATUS_BADGE: Record<string, string> = {
  pending: 'bg-yellow-100 text-yellow-800',
  approved: 'bg-blue-100 text-blue-800',
  rejected: 'bg-red-100 text-red-800',
  processed: 'bg-green-100 text-green-800',
};

const STATUS_OPTIONS = (['pending', 'approved', 'rejected', 'processed'] as const).map((s) => ({
  value: s,
  label: s,
}));

const formatDate = (raw?: string | null) => {
  if (!raw) return '—';
  try {
    return new Date(raw).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return raw;
  }
};

const AccessDenied = () => (
  <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-gray-100 bg-white py-20 text-center shadow-sm">
    <div className="flex h-14 w-14 items-center justify-center rounded-full bg-red-50">
      <ShieldAlert className="h-7 w-7 text-red-500" />
    </div>
    <h2 className="text-lg font-semibold text-gray-800">Access restricted</h2>
    <p className="max-w-sm text-sm text-gray-500">
      Viewing refund requests requires the{' '}
      <code className="rounded bg-gray-100 px-1.5 py-0.5 text-xs">payment.view</code> permission.
    </p>
  </div>
);

const RefundRequestListPage = () => {
  useBreadcrumbs([
    { label: 'Payments', href: '/admin/payments' },
    { label: 'Refund requests' },
  ]);

  const viewPerm = useHasPermission('payment.view');
  const refundPerm = useHasPermission('payment.refund');

  const canView = viewPerm.isSuperAdmin || viewPerm.hasPermission;
  const canRefund = refundPerm.isSuperAdmin || refundPerm.hasPermission;
  const permLoading = viewPerm.isLoading;

  const [notes, setNotes] = useState<Record<number, string>>({});
  const [busyRrid, setBusyRrid] = useState<number | null>(null);

  const { state, offset, patch, clearFilters } = usePaymentListUrlState();

  const listParams: RefundRequestListParams = {
    limit: state.limit,
    offset,
    ...(state.q.trim() ? { q: state.q.trim() } : {}),
    ...(state.status ? { status: state.status as RefundRequestListParams['status'] } : {}),
    ...(state.uid ? { uid: Number(state.uid) } : {}),
    ...(state.storeid ? { storeid: Number(state.storeid) } : {}),
  };

  const { data, isLoading, isError, error } = useRefundRequestListQuery(
    listParams,
    canView && !permLoading,
  );
  const resolveMutation = useResolveRefundRequestMutation();

  const requests = data?.refund_requests ?? [];
  const pagination = data?.pagination;
  const forbidden =
    (error as { status?: number })?.status === 403 || (!permLoading && !canView);

  const handleAction = async (rrid: number, action: RefundRequestAction) => {
    if (!canRefund) return;
    setBusyRrid(rrid);
    try {
      await resolveMutation.mutateAsync({
        rrid,
        action,
        notes: notes[rrid]?.trim() || undefined,
        ...(action === 'process'
          ? { idempotencyKey: generateIdempotencyKey() }
          : {}),
      });
      const labels: Record<RefundRequestAction, string> = {
        approve: 'approved',
        reject: 'rejected',
        process: 'processed',
      };
      toast.success(`Refund request ${labels[action]}`, { richColors: true });
      setNotes((prev) => {
        const next = { ...prev };
        delete next[rrid];
        return next;
      });
    } catch (err: any) {
      toast.error(err?.message || `Failed to ${action}`, { richColors: true });
    } finally {
      setBusyRrid(null);
    }
  };

  if (permLoading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="mx-auto max-w-screen-2xl space-y-4">
          <Skeleton className="h-10 w-48" />
          <Skeleton className="h-24 w-full" />
        </div>
      </div>
    );
  }

  if (forbidden) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="mx-auto max-w-screen-2xl">
          <AccessDenied />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto max-w-screen-2xl space-y-6">
        <div>
          <div className="mb-1 flex items-center gap-2.5">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-600">
              <RotateCcw className="h-4 w-4 text-white" />
            </div>
            <h1 className="text-2xl font-black text-gray-900">Refund requests</h1>
          </div>
          <p className="text-sm text-gray-500">Approve, reject, or process order refund requests.</p>
        </div>

        <PaymentsFilterBar
          state={state}
          onPatch={patch}
          onClear={() => clearFilters()}
          statusOptions={STATUS_OPTIONS}
          showAmount={false}
          showDates={false}
          showOid={false}
          showCcode={false}
          searchPlaceholder="Search by uid or id…"
        />

        {isError && !forbidden && (
          <div className="rounded-lg border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
            {(error as Error)?.message || 'Failed to load refund requests'}
          </div>
        )}

        <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-100">
              <thead>
                <tr className="bg-gray-50">
                  {['RRID', 'Order', 'Amount', 'Status', 'User', 'Date', 'Notes / Actions'].map((h) => (
                    <th
                      key={h}
                      className="px-6 py-3.5 text-left text-xs font-semibold uppercase tracking-wider text-gray-500"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {isLoading &&
                  Array.from({ length: 8 }).map((_, i) => (
                    <tr key={i}>
                      {Array.from({ length: 7 }).map((__, j) => (
                        <td key={j} className="px-6 py-4">
                          <Skeleton className="h-4 w-16" />
                        </td>
                      ))}
                    </tr>
                  ))}
                {!isLoading && requests.length === 0 && (
                  <tr>
                    <td colSpan={7} className="py-16 text-center text-sm text-gray-500">
                      No refund requests found
                    </td>
                  </tr>
                )}
                {!isLoading &&
                  requests.map((rr: RefundRequest) => {
                    const status = String(rr.status || '');
                    const isBusy = busyRrid === rr.rrid;
                    return (
                      <tr key={rr.rrid}>
                        <td className="px-6 py-4 font-mono text-sm font-semibold">{rr.rrid}</td>
                        <td className="px-6 py-4">
                          {rr.orderid != null ? (
                            <RelationshipLink kind="order" id={rr.orderid} />
                          ) : (
                            <span className="text-sm text-gray-400">—</span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm font-medium">
                          {rr.amount_requested != null ? rr.amount_requested : '—'}
                          {rr.reason ? (
                            <p className="max-w-[160px] truncate text-xs text-gray-400" title={rr.reason}>
                              {rr.reason}
                            </p>
                          ) : null}
                        </td>
                        <td className="px-6 py-4">
                          <span
                            className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
                              STATUS_BADGE[status] || STATUS_BADGE.pending
                            }`}
                          >
                            {status.toUpperCase() || '—'}
                          </span>
                        </td>
                        <td className="max-w-[160px] px-6 py-4">
                          <UserRelationshipLink uid={rr.uid} summary={rr.user_summary} />
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {formatDate(rr.date_added)}
                        </td>
                        <td className="px-6 py-4">
                          {canRefund && status !== 'processed' && status !== 'rejected' ? (
                            <div className="space-y-2">
                              <div className="space-y-1">
                                <Label className="text-xs">Notes</Label>
                                <Input
                                  value={notes[rr.rrid] ?? ''}
                                  onChange={(e) =>
                                    setNotes((prev) => ({ ...prev, [rr.rrid]: e.target.value }))
                                  }
                                  placeholder="Optional notes"
                                  className="h-8 text-sm"
                                />
                              </div>
                              <div className="flex flex-wrap gap-1.5">
                                {status === 'pending' && (
                                  <>
                                    <Button
                                      type="button"
                                      size="sm"
                                      variant="outline"
                                      disabled={isBusy}
                                      onClick={() => handleAction(rr.rrid, 'approve')}
                                    >
                                      {isBusy ? (
                                        <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                                      ) : (
                                        <Check className="mr-1 h-3.5 w-3.5" />
                                      )}
                                      Approve
                                    </Button>
                                    <Button
                                      type="button"
                                      size="sm"
                                      variant="outline"
                                      disabled={isBusy}
                                      onClick={() => handleAction(rr.rrid, 'reject')}
                                    >
                                      <Ban className="mr-1 h-3.5 w-3.5" />
                                      Reject
                                    </Button>
                                  </>
                                )}
                                {(status === 'pending' || status === 'approved') && (
                                  <Button
                                    type="button"
                                    size="sm"
                                    disabled={isBusy}
                                    onClick={() => handleAction(rr.rrid, 'process')}
                                  >
                                    {isBusy ? (
                                      <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                                    ) : (
                                      <Play className="mr-1 h-3.5 w-3.5" />
                                    )}
                                    Process
                                  </Button>
                                )}
                              </div>
                            </div>
                          ) : (
                            <p className="max-w-[220px] truncate text-sm text-gray-500">
                              {rr.notes || '—'}
                            </p>
                          )}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
          <div className="border-t border-gray-100 px-6 py-3">
            <Pagination
              total={pagination?.total ?? 0}
              page={state.page}
              itemsPerPage={state.limit}
              limit={state.limit}
              setPage={(page) => patch({ page }, false)}
              setItemsPerPage={(limit) => patch({ limit, page: 1 }, false)}
              showButtons
              showSelect
              selectOptions={[10, 25, 50, 100]}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default RefundRequestListPage;
